import { Injectable, inject } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

import { GbeEndPointV4 } from '@core/models/gbe/gbe-endpoint.enum';
import { GbeTokensService } from '@core/services/gbe-tokens/gbe-tokens.service';
import {
  USER_PREFERENCES_MUTATION_ACTIONS,
  UserPreferencesActions
} from '@core/store/user-preferences/user-preferences.actions';
import { UserPreferences } from '@core/store/user-preferences/user-preferences.model';
import {
  selectUserPreferences,
  selectUserPreferencesLoaded
} from '@core/store/user-preferences/user-preferences.selectors';
import { GBE_BASE_URL } from '@core/tokens/gbe-url.token';

/**
 * Flushes pending preference changes when the tab is hidden/closed.
 *
 * The debounced persistence effect only writes 500 ms after the last change, so
 * closing the tab inside that window would otherwise lose the change. On
 * `pagehide` we send the latest blob with a `keepalive` request that the browser
 * is allowed to complete after the document is gone. We bypass `HttpClient`
 * (and its interceptors) here on purpose: the `preferences` blob must travel
 * verbatim, which is exactly what a raw `fetch` body gives us.
 */
@Injectable({ providedIn: 'root' })
export class UserPreferencesFlushService {
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly tokens = inject(GbeTokensService);
  private readonly url = `${inject(GBE_BASE_URL)}/${GbeEndPointV4.USER_PREFERENCES}`;

  private preferences: UserPreferences = {};
  private loaded = false;
  /** A mutation happened that has not yet been confirmed persisted. */
  private dirty = false;

  constructor() {
    this.store
      .select(selectUserPreferences)
      .subscribe((preferences) => (this.preferences = preferences));
    this.store
      .select(selectUserPreferencesLoaded)
      .subscribe((loaded) => (this.loaded = loaded));

    this.actions$
      .pipe(ofType(...USER_PREFERENCES_MUTATION_ACTIONS))
      .subscribe(() => (this.dirty = true));
    this.actions$
      .pipe(ofType(UserPreferencesActions.persistSuccess))
      .subscribe(() => (this.dirty = false));
  }

  /** Registers the `pagehide` listener. Call once during app startup. */
  initialize(): void {
    window.addEventListener('pagehide', () => this.flush());
  }

  /** Sends any pending change immediately with a keepalive request. */
  flush(): void {
    if (!this.dirty || !this.loaded) return;

    const token = this.tokens.getAccessToken();
    if (!token) return;

    void fetch(this.url, {
      method: 'PUT',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ preferences: this.preferences })
    }).catch(() => undefined);

    this.dirty = false;
  }
}
